/**
 * Location chat - translating agent proposals into draft mutations.
 *
 * The chat agent proposes; it never writes. This module turns a proposal into the draft ops that
 * would carry it out, and into the overlay tree used to show the user what they are about to accept.
 * Both come from the same pass so the preview and the applied result cannot disagree.
 *
 * Everything here is pure. The proposal arrived over the network and is re-validated against the
 * tree actually on screen: ids may have been renamed, re-parented or deleted by hand since the
 * agent looked, and `location_level` is always recomputed rather than trusted.
 */

import type { LocationChatProposedAction, LocationTreeNode } from '../../../api/schedulingApis';
import { collectDescendantIds, moveNodesInTree, updateNodeInTree } from '../../../location-hierarchy/aiReviewUtils';
import { buildCloneSubtree } from '../../../location-hierarchy/locationDraftUtils';
import { insertNodeIntoTree, type LocationDraftOp } from '../../../stores/locationsTreeStore';
import {
  canPlaceLocationType,
  isFloorLocationType,
  nextFloorIndexUnderParent,
  resolveLocationTypeInput,
  withFloorNumberMeta,
} from './locationTypeRules';

/** How a node touched by a pending proposal is marked in the tree. */
export type ChatPendingMark = 'new' | 'update' | 'remove';

export interface ChatSkippedAction {
  summary: string;
  reason: string;
}

export interface ChatApplyPlan {
  /** Draft ops in proposal order. */
  ops: LocationDraftOp[];
  /** location_id -> how to mark it in the tree preview. */
  marks: Map<string, ChatPendingMark>;
  /** Actions that no longer apply to the tree on screen, with the reason to show the user. */
  skipped: ChatSkippedAction[];
}

function deepClone(nodes: LocationTreeNode[]): LocationTreeNode[] {
  return JSON.parse(JSON.stringify(nodes));
}

function findNodeById(nodes: LocationTreeNode[], id: string): LocationTreeNode | null {
  const target = String(id);
  for (const node of nodes) {
    if (String(node.location_id) === target) return node;
    const hit = node.child?.length ? findNodeById(node.child, id) : null;
    if (hit) return hit;
  }
  return null;
}

function collectLocationTypes(nodes: LocationTreeNode[]): string[] {
  const out = new Set<string>();
  const walk = (list: LocationTreeNode[]) => {
    for (const node of list) {
      if (node.location_type) out.add(node.location_type);
      if (node.child?.length) walk(node.child);
    }
  };
  walk(nodes);
  return Array.from(out);
}

/** Is `candidate` inside `root`'s subtree (so moving root under it would orphan the branch)? */
function isSelfOrDescendant(root: LocationTreeNode, candidateId: string): boolean {
  return collectDescendantIds([root]).has(String(candidateId));
}

function appendChild(parent: LocationTreeNode, node: LocationTreeNode): void {
  parent.child = Array.isArray(parent.child) ? parent.child : [];
  parent.child.push(node);
}

/**
 * Plan the draft ops for a proposal against the tree currently displayed.
 *
 * Proposal order is preserved: the agent's summary was composed in that order, so reordering would
 * make the text the user approved stop matching what runs. Each action is instead validated against
 * the state the previous ones left, and anything no longer applicable is reported in `skipped`.
 */
export function planChatApply(
  actions: LocationChatProposedAction[],
  tree: LocationTreeNode[],
): ChatApplyPlan {
  const working = deepClone(tree);
  const ops: LocationDraftOp[] = [];
  const marks = new Map<string, ChatPendingMark>();
  const skipped: ChatSkippedAction[] = [];

  const skip = (action: LocationChatProposedAction, reason: string) => {
    skipped.push({ summary: action.summary || describe(action), reason });
  };

  for (const action of actions) {
    switch (action.op) {
      case 'create': {
        const name = (action.name ?? '').trim();
        if (!name) {
          skip(action, 'No name was given.');
          break;
        }

        const parentId = action.parentLocationId ? String(action.parentLocationId) : null;
        const parent = parentId ? findNodeById(working, parentId) : null;
        if (parentId && !parent) {
          skip(action, 'The parent location is no longer in the tree.');
          break;
        }

        const type =
          resolveLocationTypeInput(action.locationType ?? '', collectLocationTypes(working)) || 'unit';
        const placement = canPlaceLocationType({
          childType: type,
          parent,
          tree: working,
          asRoot: parent == null,
        });
        if (!placement.ok) {
          skip(action, placement.reason);
          break;
        }

        // Recomputed from the tree on screen. The agent derived its locationLevel from the saved
        // tree, which is wrong the moment the user has re-parented the target by hand.
        const level = parent ? (parent.location_level ?? 1) + 1 : 1;
        const siblings = parent ? parent.child ?? [] : working;
        // The server pre-assigns ids when it proposes a whole generated hierarchy, so a child can
        // name a parent that is also new. An id already on screen is refused - reusing one would
        // make the tree ambiguous.
        const proposedId = action.locationId ? String(action.locationId) : null;
        const node: LocationTreeNode = {
          location_id:
            proposedId && !findNodeById(working, proposedId) ? proposedId : crypto.randomUUID(),
          name,
          location_type: type,
          location_level: level,
          location_group: Array.isArray(action.aliases) ? action.aliases : [],
          parent_location_id: parent?.location_id ?? null,
          // Floors need an index or sortFloorSiblingsForDisplay cannot order them against
          // siblings that already have one. The agent reads it off the name when it can - a
          // "Basement 2" is floor -2, which no sequential counter would ever produce - so prefer
          // that and fall back to the next free slot.
          meta: isFloorLocationType(type)
            ? withFloorNumberMeta(
                {},
                typeof action.floorNumber === 'number'
                  ? action.floorNumber
                  : nextFloorIndexUnderParent(siblings),
              )
            : {},
          child: [],
        };

        if (parent) appendChild(parent, node);
        else working.push(node);

        // A childless copy on purpose: `node` stays in `working` so later actions can parent to
        // it, and appending children there must not smuggle them into this op as well - they
        // carry their own add op and would otherwise be inserted twice.
        ops.push({ kind: 'add', parentId, node: { ...node, child: [] } });
        marks.set(node.location_id, 'new');
        break;
      }

      case 'update': {
        const locationId = action.locationId ? String(action.locationId) : null;
        if (!locationId) {
          skip(action, 'No location was given.');
          break;
        }
        const target = findNodeById(working, locationId);
        if (!target) {
          skip(action, 'That location is no longer in the tree.');
          break;
        }

        const floorNumber = typeof action.floorNumber === 'number' ? action.floorNumber : null;
        const name = (action.name ?? '').trim() || null;
        const aliases = Array.isArray(action.aliases)
          ? action.aliases.map((a) => String(a).trim()).filter(Boolean)
          : null;
        const newParentId = action.parentLocationId ? String(action.parentLocationId) : null;
        const movingTo =
          newParentId && newParentId !== String(target.parent_location_id ?? '') ? newParentId : null;

        // The effective parent decides which types are legal, so resolve the move first.
        const newParent = movingTo ? findNodeById(working, movingTo) : null;
        if (movingTo && !newParent) {
          skip(action, 'The new parent location is no longer in the tree.');
          break;
        }
        if (newParent && isSelfOrDescendant(target, newParent.location_id)) {
          skip(action, 'That would move a location inside itself.');
          break;
        }

        const requestedType = (action.locationType ?? '').trim();
        let type: string | null = null;
        if (requestedType) {
          type = resolveLocationTypeInput(requestedType, collectLocationTypes(working));
          const placement = canPlaceLocationType({
            childType: type,
            parent: newParent ?? findNodeById(working, String(target.parent_location_id ?? '')),
            tree: working,
            excludeNodeId: target.location_id,
            asRoot: (newParent ?? target.parent_location_id) == null,
          });
          if (!placement.ok) {
            skip(action, placement.reason);
            break;
          }
        }

        const updates: Partial<
          Pick<LocationTreeNode, 'location_type' | 'name' | 'location_group' | 'meta'>
        > = {};
        if (name) updates.name = name;
        if (type) updates.location_type = type;
        if (aliases) updates.location_group = aliases;

        if (floorNumber != null) updates.meta = withFloorNumberMeta(target.meta, floorNumber);

        if (Object.keys(updates).length === 0 && !movingTo) {
          skip(action, 'Nothing would change.');
          break;
        }

        // updateLocationDraftNode cannot re-parent, so a move is always a separate op. Order
        // matters: field changes first, then the move.
        if (Object.keys(updates).length > 0) {
          ops.push({ kind: 'update', nodeId: target.location_id, updates });
          Object.assign(target, updates);
        }
        if (movingTo && newParent) {
          // Append. A ProposedAction carries no sibling ordering, so there is nothing better to do.
          const index = newParent.child?.length ?? 0;
          ops.push({ kind: 'move', nodeId: target.location_id, parentId: movingTo, index });
          Object.assign(
            target,
            { parent_location_id: movingTo, location_level: (newParent.location_level ?? 1) + 1 },
          );
        }
        marks.set(String(target.location_id), 'update');
        break;
      }

      case 'duplicate': {
        // Expanded here rather than asked of the model: the client already has the subtree, so a
        // 40-node copy stays one proposed action instead of 40 model-authored creates.
        const locationId = action.locationId ? String(action.locationId) : null;
        if (!locationId) {
          skip(action, 'No location was given.');
          break;
        }
        const source = findNodeById(working, locationId);
        if (!source) {
          skip(action, 'That location is no longer in the tree.');
          break;
        }
        const parentId = source.parent_location_id ? String(source.parent_location_id) : null;
        const parent = parentId ? findNodeById(working, parentId) : null;
        const clone = buildCloneSubtree(source, parentId, true);

        // Match the clone button: a copied floor takes the next free index and is named for it.
        if (isFloorLocationType(clone.location_type)) {
          const siblings = parent ? parent.child ?? [] : working;
          const nextIndex = nextFloorIndexUnderParent(siblings);
          clone.name = `Floor ${nextIndex}`;
          clone.meta = withFloorNumberMeta(clone.meta, nextIndex);
        }

        if (parent) appendChild(parent, clone);
        else working.push(clone);

        // Flatten parent-first so each node's add op can reference an id emitted before it.
        const flatten = (n: LocationTreeNode, pid: string | null, level: number) => {
          ops.push({
            kind: 'add',
            parentId: pid,
            node: { ...n, parent_location_id: pid, location_level: level, child: [] },
          });
          marks.set(n.location_id, 'new');
          (n.child ?? []).forEach((c) => flatten(c, n.location_id, level + 1));
        };
        flatten(clone, parentId, parent ? (parent.location_level ?? 1) + 1 : 1);
        break;
      }

      case 'delete': {
        const locationId = action.locationId ? String(action.locationId) : null;
        if (!locationId) {
          skip(action, 'No location was given.');
          break;
        }
        const target = findNodeById(working, locationId);
        if (!target) {
          skip(action, 'That location is no longer in the tree.');
          break;
        }
        // The agent only proposes deleting childless locations, but the draft may have gained
        // children since. Save derives its DELETE list from removedIds, so they must all be in it.
        const ids = Array.from(collectDescendantIds([target]));
        ops.push({ kind: 'remove', ids });
        ids.forEach((id) => marks.set(String(id), 'remove'));
        break;
      }

      default:
        skip(action, 'Unsupported change.');
    }
  }

  return { ops, marks, skipped };
}

function describe(action: LocationChatProposedAction): string {
  return `${action.op} ${action.name ?? action.locationId ?? ''}`.trim();
}

/**
 * Fold a plan's ops into a throwaway copy of the tree, for display only.
 *
 * `remove` ops are deliberately NOT folded: a location the user is being asked to delete has to stay
 * on screen, marked, or there is nothing to point at when asking them to confirm.
 */
export function buildChatPreviewTree(
  tree: LocationTreeNode[],
  ops: LocationDraftOp[],
): LocationTreeNode[] {
  let next = tree;
  for (const op of ops) {
    if (op.kind === 'add') {
      next = insertNodeIntoTree(next, op.parentId, op.node);
    } else if (op.kind === 'update') {
      next = updateNodeInTree(next, op.nodeId, op.updates);
    } else if (op.kind === 'move') {
      next = moveNodesInTree(next, [op.nodeId], op.parentId, op.index);
    }
  }
  return next;
}
